/**
 * Losing the network, and getting it back.
 *
 * A mail client is used on trains, in basements and on hotel wifi, so this is not an edge case — it
 * is a normal condition that happens several times a day. The failure to avoid is not "we went
 * offline" but **"we came back and the screen was still lying"**.
 */

export type Connection = 'online' | 'offline' | 'reconnecting' | 'degraded';

export interface StalenessInput {
  readonly lastLoadedAt: Date | null;
  readonly connection: Connection;
  readonly now: Date;
}

/**
 * When to say the screen may be out of date.
 *
 * Sixty seconds offline. Not immediately: a two-second connection blip while somebody is reading is
 * not worth a banner, and a banner that appears for every blip is a banner people learn to ignore
 * before the one that mattered.
 */
export const STALE_AFTER_SECONDS = 60;

export function isStale(input: StalenessInput): boolean {
  if (input.connection === 'online') return false;
  if (input.lastLoadedAt === null) return true;
  return (input.now.getTime() - input.lastLoadedAt.getTime()) / 1000 > STALE_AFTER_SECONDS;
}

/**
 * What to do on reconnecting.
 *
 * Refresh, and **do not restore scroll or selection blindly**. The list may have changed: restoring
 * a selection by index after a refresh selects whatever moved into that position, and the next
 * action is applied to a message nobody chose.
 */
export type ReconnectAction = 'refresh' | 'refresh_preserving_selection' | 'reload' | 'nothing';

export function onReconnect(input: {
  offlineSeconds: number;
  hasSelection: boolean;
  hasUnsavedDraft: boolean;
}): ReconnectAction {
  /*
   * An unsaved draft outranks freshness.
   *
   * A refresh that discards what somebody typed while offline is the worst thing this function can
   * do, and it is what a naive "reload on reconnect" does every time.
   */
  if (input.hasUnsavedDraft) return 'nothing';

  /* A long absence means the schema, the session or the version may have moved on. */
  if (input.offlineSeconds > 3600) return 'reload';

  return input.hasSelection ? 'refresh_preserving_selection' : 'refresh';
}

/**
 * Requests made while offline.
 *
 * Reads are dropped: the answer would be stale by the time the connection returns, and a queue of
 * reads that all fire at once on reconnect is a self-inflicted thundering herd.
 *
 * Writes are queued **only if idempotent**. A queued write with no idempotency key is a write that
 * may have already happened — the request left, the response did not come back — and replaying it
 * archives twice or sends twice.
 */
export function mayQueue(input: { method: string; hasIdempotencyKey: boolean }): boolean {
  if (input.method === 'GET' || input.method === 'HEAD') return false;
  return input.hasIdempotencyKey;
}

/**
 * How long a queued write is worth keeping.
 *
 * Ten minutes. Past that the mailbox has probably changed underneath it, and applying an intention
 * from a quarter of an hour ago to a list that has moved on is how somebody's mail ends up
 * somewhere they did not put it.
 */
export const QUEUE_TTL_MS = 10 * 60 * 1000;

export function queueExpired(queuedAt: Date, now: Date): boolean {
  return now.getTime() - queuedAt.getTime() > QUEUE_TTL_MS;
}

/**
 * What the banner says.
 *
 * One line, and it never blocks. Everything already on the screen is still true — it is just not
 * getting any newer — and hiding it behind a modal because the *next* request failed takes away
 * what somebody has.
 */
export interface Banner {
  readonly key: string;
  readonly tone: 'info' | 'warning';
  readonly dismissible: boolean;
}

export function bannerFor(input: StalenessInput): Banner | null {
  if (input.connection === 'online') return null;

  if (input.connection === 'reconnecting') {
    return { key: 'network.reconnecting', tone: 'info', dismissible: false };
  }

  if (input.connection === 'degraded') {
    return { key: 'network.degraded', tone: 'info', dismissible: true };
  }

  return isStale(input)
    ? { key: 'network.offlineStale', tone: 'warning', dismissible: false }
    : { key: 'network.offline', tone: 'info', dismissible: true };
}
